import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Box,
  Button,
  Input,
  VStack,
  Text,
  Heading,
  Flex,
  Stack,
  Link as ChakraLink,
  Field,
} from '@chakra-ui/react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useEffect } from 'react'
import { Mail, Lock, AlertCircle, Users, FileText, Calendar } from 'lucide-react'
import { useExtranetLogin } from '../hooks/useExtranetAuth'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '@/lib/supabase'
import type { LoginCredentials } from '../types'

const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
})

export function ExtranetLoginPage() {
  const navigate = useNavigate()
  const login = useExtranetLogin()
  const { data: auth, isLoading } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
  })

  useEffect(() => {
    // Only redirect if already authenticated (e.g., page refresh)
    if (isLoading) {
      return
    }

    if (auth?.session && !login.isPending) {
      // Check if user has extranet access
      const hasExtranetRole = auth.residences.some((r) =>
        ['OWNER', 'COUNCIL_MEMBER'].includes(r.role)
      )
      if (hasExtranetRole) {
        navigate('/extranet/dashboard')
      }
    }
  }, [auth, isLoading, navigate, login.isPending])

  const onSubmit = async (data: LoginCredentials) => {
    login.mutate(data, {
      onSuccess: async () => {
        // Wait for queries to refetch
        await new Promise(resolve => setTimeout(resolve, 100))

        // Get fresh auth data
        const { data: { session } } = await supabase.auth.getSession()

        if (!session) return

        // Check user residences
        const { data: userResidences } = await supabase
          .from('user_residences')
          .select('role')
          .eq('user_id', session.user.id)

        // Navigate if user has extranet access
        if (userResidences?.some(r => ['OWNER', 'COUNCIL_MEMBER'].includes(r.role))) {
          navigate('/extranet/dashboard')
        }
      }
    })
  }

  const getErrorMessage = (error: any) => {
    if (error?.message === 'EXTRANET_ACCESS_DENIED') {
      return "Accès refusé. Vous n'avez pas accès à l'extranet copropriétaire."
    }
    return 'Identifiants invalides. Veuillez réessayer.'
  }

  return (
    <Flex minH="100vh">
      {/* Left side - Login Form */}
      <Flex
        flex={1}
        bg="white"
        align="center"
        justify="center"
        px={{ base: 6, md: 12, lg: 16 }}
        py={12}
      >
        <Box w="full" maxW="440px">
          {/* Logo */}
          <Box mb={8} textAlign="center">
            <Flex direction="column" align="center" gap={3}>
              <img
                src="/assets/logo-compact-light.svg"
                alt="Kopro"
                style={{ height: '70px', width: 'auto' }}
              />
              <Box>
                <Heading size="xl" fontWeight="bold" color="gray.900">
                  Kopro
                </Heading>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Espace Copropriétaire
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* Form Card */}
          <Box
            bg="white"
            p={8}
            borderRadius="2xl"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="lg"
          >
            {/* Heading */}
            <Stack gap={2} mb={8}>
              <Heading size="xl" fontWeight="bold" color="gray.900">
                Bienvenue ! 👋
              </Heading>
              <Text color="gray.600" fontSize="md">
                Connectez-vous à votre espace personnel
              </Text>
            </Stack>

            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={5} align="stretch">
              {/* Email Field */}
              <Field.Root invalid={!!errors.email} w="full">
                <Field.Label fontSize="sm" fontWeight="medium" color="gray.700">
                  Adresse email
                </Field.Label>
                <Box position="relative" w='100%'>
                  <Box
                    position="absolute"
                    left={3}
                    top="50%"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                  >
                    <Mail size={18} color="#a0aec0" />
                  </Box>
                  <Input
                    {...register('email')}
                    type="email"
                    placeholder="votre@email.com"
                    size="lg"
                    pl={10}
                    borderColor="gray.300"
                    _hover={{ borderColor: 'brand.400' }}
                    _focus={{
                      borderColor: 'brand.800',
                      boxShadow: '0 0 0 1px var(--chakra-colors-brand-800)',
                      outline: 'none'
                    }}
                  />
                </Box>
                {errors.email && (
                  <Field.ErrorText>
                    {errors.email.message}
                  </Field.ErrorText>
                )}
              </Field.Root>

              {/* Password Field */}
              <Field.Root invalid={!!errors.password} w="full">
                <Field.Label fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Mot de passe
                </Field.Label>
                <Box position="relative" w='100%'>
                  <Box
                    position="absolute"
                    left={3}
                    top="50%"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                  >
                    <Lock size={18} color="#a0aec0" />
                  </Box>
                  <Input
                    {...register('password')}
                    type="password"
                    placeholder="••••••••"
                    size="lg"
                    pl={10}
                    borderColor="gray.300"
                    _hover={{ borderColor: 'brand.400' }}
                    _focus={{
                      borderColor: 'brand.800',
                      boxShadow: '0 0 0 1px var(--chakra-colors-brand-800)',
                      outline: 'none'
                    }}
                  />
                </Box>
                {errors.password && (
                  <Field.ErrorText>
                    {errors.password.message}
                  </Field.ErrorText>
                )}
              </Field.Root>

              {/* Forgot Password Link */}
              <Box w="full" textAlign="right">
                <ChakraLink
                  fontSize="sm"
                  color="brand.800"
                  fontWeight="medium"
                  _hover={{ color: 'brand.900', textDecoration: 'underline' }}
                >
                  Mot de passe oublié ?
                </ChakraLink>
              </Box>

              {/* Error Message */}
              {login.isError && (
                <Box
                  w="full"
                  p={3}
                  bg="red.50"
                  borderRadius="lg"
                  borderLeft="4px solid"
                  borderColor="red.500"
                >
                  <Flex align="start" gap={2}>
                    <AlertCircle size={18} color="#e53e3e" />
                    <Text color="red.700" fontSize="sm" flex={1}>
                      {getErrorMessage(login.error)}
                    </Text>
                  </Flex>
                </Box>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                w="full"
                bg="brand.800"
                color="white"
                fontWeight="semibold"
                loading={login.isPending}
                _hover={{
                  bg: 'brand.900',
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                _active={{
                  bg: 'brand.900',
                  transform: 'translateY(0)',
                }}
                transition="all 0.2s"
              >
                Accéder à mon espace
              </Button>

              {/* Link to Intranet */}
              <Box w="full" textAlign="center" pt={4} borderTop="1px solid" borderColor="gray.200">
                <Text fontSize="sm" color="gray.600">
                  Vous êtes gestionnaire ?{' '}
                  <RouterLink to="/app/login" style={{ textDecoration: 'none' }}>
                    <ChakraLink
                      color="brand.800"
                      fontWeight="medium"
                      _hover={{ color: 'brand.900', textDecoration: 'underline' }}
                    >
                      Connexion outil de gestion
                    </ChakraLink>
                  </RouterLink>
                </Text>
              </Box>
            </VStack>
          </form>
          </Box>
        </Box>
      </Flex>

      {/* Right side - Image background */}
      <Flex
        flex={1}
        align="center"
        justify="center"
        display={{ base: 'none', lg: 'flex' }}
        position="relative"
        overflow="hidden"
        backgroundImage="url('/assets/modern-property.avif')"
        backgroundSize="cover"
        backgroundPosition="center"
      >
        {/* Overlay for better text readability */}
        <Box
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          bg="brand.600"
          opacity={0.85}
        />

        <VStack gap={8} zIndex={1} px={12} textAlign="center">
          <Box mb={4}>
            <img
              src="/assets/logo inline dark.svg"
              alt="Kopro"
              style={{ height: '80px', width: 'auto' }}
            />
          </Box>
          <Heading size="2xl" color="white" fontWeight="bold" maxW="500px">
            Votre copropriété au bout des doigts
          </Heading>
          <Text fontSize="lg" color="whiteAlpha.900" maxW="450px" mb={6}>
            Accédez à vos documents, suivez les assemblées générales et restez informé en temps réel
          </Text>

          {/* Feature pills */}
          <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
            <Flex
              bg="whiteAlpha.200"
              backdropFilter="blur(10px)"
              px={4}
              py={3}
              borderRadius="full"
              align="center"
              gap={2}
            >
              <FileText size={20} color="white" />
              <Text color="white" fontSize="sm" fontWeight="medium">
                Mes documents
              </Text>
            </Flex>
            <Flex
              bg="whiteAlpha.200"
              backdropFilter="blur(10px)"
              px={4}
              py={3}
              borderRadius="full"
              align="center"
              gap={2}
            >
              <Calendar size={20} color="white" />
              <Text color="white" fontSize="sm" fontWeight="medium">
                Assemblées
              </Text>
            </Flex>
            <Flex
              bg="whiteAlpha.200"
              backdropFilter="blur(10px)"
              px={4}
              py={3}
              borderRadius="full"
              align="center"
              gap={2}
            >
              <Users size={20} color="white" />
              <Text color="white" fontSize="sm" fontWeight="medium">
                Contact syndic
              </Text>
            </Flex>
          </Stack>
        </VStack>
      </Flex>
    </Flex>
  )
}
